import { useCallback, useEffect, useRef, useState } from 'react'
import { useWebSocket } from '../../hooks/useWebSocket.ts'
import { getWsUrl } from '../../lib/api.ts'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Button } from '@/components/ui/button'

type LogLevel = 'INFO' | 'WARN' | 'ERROR' | 'SUCCESS' | 'DEBUG'

interface LogEntry {
  id: string
  timestamp: string
  level: LogLevel
  message: string
  context?: Record<string, unknown>
}

function levelColor(level: LogLevel): string {
  switch (level) {
    case 'INFO': return '#999999'
    case 'WARN': return 'var(--amber)'
    case 'ERROR': return 'var(--red)'
    case 'SUCCESS': return 'var(--green)'
    case 'DEBUG': return '#555555'
    default: return '#999999'
  }
}

function msgColor(level: LogLevel): string {
  switch (level) {
    case 'WARN': return 'var(--amber)'
    case 'ERROR': return 'var(--red)'
    case 'SUCCESS': return 'var(--green)'
    default: return 'var(--text-primary)'
  }
}

function formatTime(ts: string | number): string {
  const d = typeof ts === 'number' ? new Date(ts) : new Date(ts)
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  const s = String(d.getSeconds()).padStart(2, '0')
  return `${h}:${m}:${s}`
}

const LEVEL_OPTIONS: Array<LogLevel | 'ALL'> = ['ALL', 'INFO', 'WARN', 'ERROR', 'SUCCESS', 'DEBUG']

export function LiveLogs() {
  const [entries, setEntries] = useState<LogEntry[]>([])
  const [levelFilter, setLevelFilter] = useState<LogLevel | 'ALL'>('ALL')
  const [textFilter, setTextFilter] = useState('')
  const [autoScroll, setAutoScroll] = useState(true)
  const streamRef = useRef<HTMLDivElement>(null)

  const wsUrl = getWsUrl()

  const { connected, subscribe } = useWebSocket(wsUrl)

  // Subscribe to the 'logs' channel once connected, and handle incoming log messages
  useEffect(() => {
    if (!connected) return
    subscribe(['logs'])
  }, [connected, subscribe])

  // Listen on the shared /ws connection for log messages via a raw WebSocket listener
  useEffect(() => {
    const ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'subscribe', channels: ['logs'] }))
    }

    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data as string) as { type: string; data?: unknown }
        if (data.type !== 'log') return

        const payload = data.data as { level?: string; message?: string; timestamp?: string | number; context?: Record<string, unknown> }
        const entry: LogEntry = {
          id: `${Date.now()}-${Math.random()}`,
          level: (payload.level?.toUpperCase() ?? 'INFO') as LogLevel,
          message: payload.message ?? '',
          timestamp: String(payload.timestamp ?? Date.now()),
          context: payload.context,
        }
        setEntries((prev) => {
          const next = [...prev, entry]
          return next.length > 1000 ? next.slice(-1000) : next
        })
      } catch {
        // skip malformed messages
      }
    }

    return () => {
      ws.close()
    }
  }, [wsUrl])

  // Auto-scroll
  useEffect(() => {
    if (!autoScroll) return
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight
    }
  }, [entries, autoScroll])

  const handleScroll = useCallback(() => {
    if (!streamRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = streamRef.current
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 40
    setAutoScroll(isAtBottom)
  }, [])

  const visibleEntries = entries.filter((e) => {
    if (levelFilter !== 'ALL' && e.level !== levelFilter) return false
    if (textFilter && !e.message.toLowerCase().includes(textFilter.toLowerCase())) return false
    return true
  })

  const clearLogs = () => setEntries([])

  return (
    <div className="animate-fade-in-up" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="log-terminal" style={{ flex: 1, minHeight: 0 }}>
        {/* Toolbar */}
        <div className="log-toolbar">
          <Select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value as LogLevel | 'ALL')}
            style={{ width: '90px' }}
          >
            {LEVEL_OPTIONS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </Select>
          <Input
            placeholder="Filter messages…"
            value={textFilter}
            onChange={(e) => setTextFilter(e.target.value)}
            style={{ flex: 1 }}
          />
          <Button
            variant={autoScroll ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setAutoScroll((v) => !v)}
            type="button"
          >
            {autoScroll ? '⇩ AUTO' : '⇩ MANUAL'}
          </Button>
          <Button variant="ghost" size="sm" onClick={clearLogs} type="button">
            ✕ Clear
          </Button>
          <span className="font-mono" style={{ fontSize: '9px', color: 'var(--text-muted)', marginLeft: '4px' }}>
            {visibleEntries.length} lines
          </span>
        </div>

        {/* Log stream — use ScrollArea for styled scrollbar */}
        <ScrollArea className="flex-1" style={{ height: '100%' }}>
          <div
            ref={streamRef}
            className="log-stream"
            onScroll={handleScroll}
          >
            {visibleEntries.length === 0 && (
              <div style={{ padding: '20px 0', color: 'var(--text-muted)', fontSize: '11px' }}>
                Waiting for log messages
                <span className="animate-blink" style={{ color: 'var(--green)', marginLeft: '4px' }}>▋</span>
              </div>
            )}
            {visibleEntries.map((entry) => (
              <div key={entry.id} className="log-line">
                <span className="log-time">{formatTime(entry.timestamp)}</span>
                <span className="log-level" style={{ color: levelColor(entry.level) }}>
                  {entry.level.slice(0, 5).padEnd(5)}
                </span>
                <span className="log-msg" style={{ color: msgColor(entry.level) }}>
                  {entry.message}
                </span>
              </div>
            ))}
            {autoScroll && (
              <div style={{ display: 'flex', alignItems: 'center', paddingTop: '2px' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)' }}>{'>'}</span>
                <span className="log-cursor animate-blink" style={{ marginLeft: '4px' }} />
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  )
}
